import { useEffect, useMemo, useState } from "react";
import { GroupedBarChart, MultiLineChart } from "../ChartComponents";
import DashboardMetricsService from "../../services/dashboardMetricsService";
import { useDashboardUI } from "../../context/GlobalContext";
import {
    buildAnalyticsQueryParams,
    formatRole,
    formatUtcToLocal,
} from "../../helpers/helpers";

const TOP_USERS_LIMIT = 10;

const BARS = [
    { dataKey: "total_users", name: "Total Users", fill: "#6366F1" },
    { dataKey: "active_users", name: "Active Users", fill: "#22C55E" },
];

const TOTAL_USERS_LINE = [
    { dataKey: "total_users", name: "Total Users", stroke: "#6366F1" },
];

export default function UserAnalytics() {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const {
        selectedFacilityFilter,
        selectedStartDateFilter,
        selectedEndDateFilter,
        selectedClientFilter,
    } = useDashboardUI();

    useEffect(() => {
        let cancelled = false;

        const fetchMetrics = async () => {
            try {
                setLoading(true);
                setError(false);

                const queryParams = buildAnalyticsQueryParams({
                    startValue: selectedStartDateFilter,
                    endValue: selectedEndDateFilter,
                    facilityId: selectedFacilityFilter?.facility_number,
                    extra: {
                        client_app: selectedClientFilter,
                        limit: TOP_USERS_LIMIT,
                    },
                });

                const response =
                    await DashboardMetricsService.getUserMetrics(queryParams);
                if (!cancelled) setMetrics(response?.data);
            } catch (err) {
                console.error("Failed to fetch user metrics", err);
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchMetrics();
        return () => {
            cancelled = true;
        };
    }, [
        selectedStartDateFilter,
        selectedEndDateFilter,
        selectedFacilityFilter,
        selectedClientFilter,
    ]);

    // A KPI that fails server-side comes back as an empty array with its name in
    // `_errors`, so an empty list on its own doesn't tell us whether there was
    // nothing to show or nothing could be read. The flags below keep the two apart.
    const failedKpis = metrics?._errors || [];
    const topUsersFailed = failedKpis.includes("top_users");
    const dailyUsersFailed = failedKpis.includes("total_vs_active_users");

    const topUsers = metrics?.top_users || [];

    const dailyUsers = useMemo(() => {
        return (metrics?.total_vs_active_users || []).map((item) => ({
            date: item.date,
            total_users: item.total_users || 0,
            active_users: item.active_users || 0,
        }));
    }, [metrics]);

    const latestTotalUsers = dailyUsers.length
        ? dailyUsers[dailyUsers.length - 1].total_users
        : 0;
    const peakActiveUsers = dailyUsers.reduce(
        (max, item) => Math.max(max, item.active_users),
        0
    );

    if (loading) {
        return (
            <div className="rounded-xl border bg-white p-6 shadow-sm">
                <div className="flex h-[300px] items-center justify-center text-sm text-slate-400">
                    Loading user analytics…
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-xl border bg-white p-6 shadow-sm">
                <div className="flex h-[300px] items-center justify-center text-sm text-red-500">
                    Failed to load user analytics
                </div>
            </div>
        );
    }

    return (
        <div className="min-w-0 w-full space-y-6 rounded-xl border bg-white p-6 shadow-sm">
            {/* Total vs active users per day — full width */}
            <div className="min-w-0 w-full rounded-xl border bg-white p-6 shadow-sm">
                <div className="mb-4">
                    <h2 className="text-lg font-semibold text-slate-900">
                        Total vs Active Users
                    </h2>
                    <p className="text-sm text-slate-500">
                        Registered users and users active on each day
                    </p>
                </div>

                {dailyUsers.length > 0 ? (
                    <>
                        <GroupedBarChart data={dailyUsers} xKey="date" bars={BARS} />

                        <div className="mt-4 flex flex-wrap gap-6 border-t pt-4 text-sm">
                            <div className="flex items-center gap-2">
                                <span
                                    className="inline-block h-2 w-2 rounded-full"
                                    style={{ backgroundColor: BARS[0].fill }}
                                />
                                <span className="text-slate-600">Total Users:</span>
                                <span className="font-semibold text-slate-900">
                                    {latestTotalUsers}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span
                                    className="inline-block h-2 w-2 rounded-full"
                                    style={{ backgroundColor: BARS[1].fill }}
                                />
                                <span className="text-slate-600">Peak Active Users:</span>
                                <span className="font-semibold text-slate-900">
                                    {peakActiveUsers}
                                </span>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex h-[300px] items-center justify-center text-sm text-slate-400">
                        {dailyUsersFailed
                            ? "Could not load total vs active users"
                            : "No user activity in this time range"}
                    </div>
                )}
            </div>

            <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Total users trend */}
                <div className="min-w-0 w-full rounded-xl border bg-white p-6 shadow-sm">
                    <div className="mb-4">
                        <h2 className="text-lg font-semibold text-slate-900">
                            Total Users Over Time
                        </h2>
                        <p className="text-sm text-slate-500">
                            Registered users as of each day
                        </p>
                    </div>

                    {dailyUsers.length > 0 ? (
                        <MultiLineChart
                            data={dailyUsers}
                            xKey="date"
                            lines={TOTAL_USERS_LINE}
                        />
                    ) : (
                        <div className="flex h-[300px] items-center justify-center text-sm text-slate-400">
                            {dailyUsersFailed
                                ? "Could not load total users"
                                : "No user activity in this time range"}
                        </div>
                    )}
                </div>

                {/* Top users leaderboard */}
                <div className="min-w-0 w-full overflow-hidden rounded-2xl border border-[#ECEEF4] bg-white">
                    <div className="px-5 pb-4 pt-5 sm:px-6">
                        <h2 className="text-base font-bold tracking-tight text-[#14122B]">
                            Top {TOP_USERS_LIMIT} Users by Logins
                        </h2>
                        <p className="mt-0.5 text-xs text-[#9AA1B2]">
                            Ranked by number of logins in this time range
                        </p>
                    </div>

                    {topUsers.length > 0 ? (
                        <div className="table-scroll overflow-auto max-h-[300px]">
                            <table className="w-full min-w-[460px] border-collapse text-sm">
                                <thead className="sticky top-0 z-10">
                                    <tr className="border-y border-[#ECEEF4] bg-[#FAFAFD] text-[11px] font-semibold uppercase tracking-wide text-[#9AA1B2]">
                                        <th className="w-14 py-2.5 pl-5 pr-2 text-left sm:pl-6">#</th>
                                        <th className="py-2.5 px-2 text-left">Name</th>
                                        <th className="py-2.5 px-2 text-center">Active Days</th>
                                        <th className="py-2.5 pl-2 pr-5 text-right sm:pr-6">Last Login</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {topUsers.map((user, index) => (
                                        <tr
                                            key={user.user_id}
                                            className="border-b border-[#F1F2F7] last:border-b-0 hover:bg-gray-50 transition-colors"
                                        >
                                            <td className="py-3 pl-5 pr-2 align-middle text-sm font-semibold text-[#7C3AED] sm:pl-6">{index + 1}</td>
                                            <td className="max-w-0 py-3 px-2 align-middle">
                                                <div className="truncate text-sm font-bold text-[#14122B]" title={`${user.first_name} ${user.last_name}`}>
                                                    {user.first_name} {user.last_name}
                                                </div>
                                                <div className="truncate text-xs text-[#9AA1B2]" title={user.email}>{user.email}</div>
                                                <div className="truncate text-xs text-[#9AA1B2]">
                                                    {formatRole(user.user_role)}
                                                </div>
                                            </td>
                                            <td className="py-3 px-2 text-center align-middle text-sm font-semibold text-[#14122B]">
                                                {user.active_days ?? 0}
                                            </td>
                                            <td className="py-3 pl-2 pr-5 text-right align-middle text-sm text-[#4B5266] whitespace-nowrap sm:pr-6">
                                                {formatUtcToLocal(user.last_login, "MMM D, YYYY")}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="mx-5 mb-5 flex items-center justify-center h-[200px] text-sm text-[#9AA1B2] border-2 border-dashed border-[#ECEEF4] rounded-xl sm:mx-6 sm:mb-6">
                            {topUsersFailed
                                ? "Could not load top users"
                                : "No user login data available"}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
